import type { ChatType } from '@form-factory/schema';
import type { EvalTestResults } from '@form-factory/schema/contracts';
import { DateFormat, formatUtils } from '@form-factory/shared';
import { useLingui } from '@lingui/react/macro';
import { useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';

/**
 * Hook for generating and downloading the Excel file with the evaluation results.
 */
export function useEvaluationXls() {
  const { t } = useLingui();
  const detailHeaders = useMemo(() => {
    return [
      t`Thread ID`,
      t`Message index`,
      t`Question`,
      t`Answer`,
      t`Expected answer`,
      t`Correct`,
      t`Accuracy`,
      t`Explanation`,
      t`Latency`,
    ];
  }, [t]);

  /**
   * Generates the Excel workbook from the evaluation results
   */
  const generateWorkbook = useCallback(
    (data: EvalTestResults, chat?: ChatType) => {
      const wb = XLSX.utils.book_new();

      // Generate summary data
      const filteredMessages = data.filter(
        test => test.messages.length > 0 && test.messages[0]?.results,
      );

      const totalMessages = filteredMessages.reduce(
        (acc, test) => acc + test.messages.length,
        0,
      );

      const totalCorrect = filteredMessages.reduce(
        (acc, test) =>
          acc + test.messages.filter(m => m.results.isCorrect).length,
        0,
      );

      let medianAccuracy = filteredMessages.reduce(
        (acc, test) => acc + test.messages[0].results.accuracyPercentage,
        0,
      );
      medianAccuracy = medianAccuracy > 0 ? medianAccuracy / data.length : 0;

      // Summary worksheet
      const summaryData = [
        ['Evaluation Summary'],
        ['Date', new Date().toLocaleString()],
        ['Total Messages', totalMessages],
        ['Correct Answers', totalCorrect],
        ['Incorrect Answers', totalMessages - totalCorrect],
        ['Median Accuracy', formatUtils.percentage(medianAccuracy)],
      ];

      // Add chat configuration if available
      if (chat) {
        summaryData.push(
          [''],
          ['Chat Configuration'],
          ['Chat ID', chat.id],
          ['Chat Name', chat.name],
          ['Agent', chat.agent],
          ['Model', chat.model],
          ...(Object.entries(chat.agentConfig || {}) as any)
            .filter(([key]: any[]) => !['systemPrompt', 'tone'].includes(key))
            .map(([key, value]: any[]) => [key, value]),
        );
      }

      const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);

      // Details worksheet
      const detailsData = data.flatMap((result, threadIndex) =>
        result.messages.map((message, messageIndex) => [
          `Thread ${threadIndex + 1}`,
          messageIndex + 1,
          message.question,
          message.answer,
          message.expectedOutput,
          message.results.isCorrect ? t`Yes` : t`No`,
          formatUtils.percentage(message.results.accuracyPercentage),
          message.results.explanation,
          `${message.latency}ms`,
        ]),
      );

      const detailsWs = XLSX.utils.aoa_to_sheet([
        detailHeaders,
        ...detailsData,
      ]);

      // Add worksheets to workbook
      XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');
      XLSX.utils.book_append_sheet(wb, detailsWs, 'Details');

      // Apply some styling
      summaryWs['!cols'] = [{ wch: 15 }, { wch: 50 }];
      detailsWs['!cols'] = detailHeaders.map(() => ({ wch: 20 }));

      return wb;
    },
    [detailHeaders, t],
  );

  /**
   * Downloads the Excel file with the evaluation results
   */
  const downloadXls = useCallback(
    (data: EvalTestResults, chatId: string, chat?: ChatType) => {
      const wb = generateWorkbook(data, chat);
      XLSX.writeFile(
        wb,
        `${formatUtils.date(new Date(), DateFormat.DateInput)}_${t`evaluation`}_${chatId}_${chat?.agent}_${chat?.name}.xlsx`,
      );
    },
    [generateWorkbook, t],
  );

  return { generateWorkbook, downloadXls };
}
